// Third Party
import cx from 'classnames';
import { twMerge } from 'tailwind-merge';

// Utils
import { ButtonColor, ButtonSize, ButtonVariant } from '.';
import { getSizeStyleAsClassName, getVariantStyleAsClassName } from './utils';

import { ButtonExtendProps } from './button';

type ButtonStylesProps = Pick<
  ButtonExtendProps,
  | 'isFullWidth'
  | 'disabled'
  | 'color'
  | 'isCircle'
  | 'size'
  | 'variant'
  | 'disabled'
  | 'isLoading'
>;

export const getButtonStylesAsClassName = ({
  isFullWidth = false,
  disabled = false,
  color = 'primary',
  size = 'md',
  variant = 'filled',
  isLoading = false,
}: ButtonStylesProps): string => {
  const staticStyles = cx(
    'relative',
    'inline-flex',
    'items-center',
    'font-semibold',
    'no-underline',
    'cursor-pointer',
    'transition-all',
    'duration-200',
    'ease-in-out',
    'box-border',
    'rounded-full',
    'text-center'
  );

  const fullWidthStyles = cx({
    'w-full flex justify-center': isFullWidth,
  });

  const sizeVaryingStyles = getSizeStyleAsClassName(size, variant, disabled);

  const variantVaryingStyles = cx({
    [getVariantStyleAsClassName(color, variant)]: !disabled,
  });

  const disabledVaryingStyles = cx({
    'cursor-not-allowed': disabled,
    'bg-[#8e9899] text-white':
      disabled && color === 'primary' && variant !== 'text',
    'bg-[#f1d496] text-white':
      disabled && color === 'secondary' && variant !== 'text',
    'bg-[#b0afa5] text-white':
      disabled && color === 'gray' && variant !== 'text',
    'bg-[#7a7875] text-white':
      disabled && color === 'black' && variant !== 'text',

    'bg-transparent text-[#8e9899]':
      color === 'primary' && disabled && variant === 'text',
    'bg-transparent text-[#f1d496]':
      color === 'secondary' && disabled && variant === 'text',
    'bg-transparent text-[#b0afa5]':
      color === 'gray' && disabled && variant === 'text',
    'bg-transparent text-[#7a7875]':
      color === 'black' && disabled && variant === 'text',
  });

  const isLoadingVariantStyles = cx({
    'text-transparent pointer-events-none': isLoading,
  });

  const basicButtonStyles = 'bg-transparent';

  // the later the parameter, the higher the precedence
  const buttonStylesClassName = twMerge(
    basicButtonStyles,
    staticStyles,
    fullWidthStyles,
    sizeVaryingStyles,
    variantVaryingStyles,
    disabledVaryingStyles,
    isLoadingVariantStyles
  );

  return buttonStylesClassName;
};

export const getButtonComponentStylesAsClassName = (
  isFullWidth: boolean,
  color: ButtonColor,
  isCircle: boolean,
  size: ButtonSize,
  variant: ButtonVariant,
  disabled: boolean,
  isLoading: boolean
): string => {
  return getButtonStylesAsClassName({
    isFullWidth,
    color,
    isCircle,
    size,
    variant,
    disabled,
    isLoading,
  });
};
